import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { SessionRole } from '@crowdctrl/core';
import { SessionMemberService } from './session-member.service';
import { shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SessionRoleService {
  private sessionMemberService = inject(SessionMemberService);

  private roleSubject = new BehaviorSubject<SessionRole | null>(null);
  private cachedSessionId: string | null = null;
  private cachedUserId: string | null = null;
  private cachedWorkspaceId: string | null = null;
  private loading$: Observable<SessionRole | null> | null = null;

  role$ = this.roleSubject.asObservable();

  getRoleValue(): SessionRole | null {
    return this.roleSubject.value;
  }

  ensureRole$(
    sessionId: string,
    userId: string,
    workspaceId: string,
    forceRefresh: boolean = false,
  ): Observable<SessionRole | null> {
    // Clear if session, user, or workspace changed
    if (
      this.cachedSessionId !== sessionId ||
      this.cachedUserId !== userId ||
      this.cachedWorkspaceId !== workspaceId
    ) {
      this.roleSubject.next(null);
      this.loading$ = null;
    }

    // Return cached if available and not forcing refresh
    if (
      !forceRefresh &&
      this.cachedSessionId === sessionId &&
      this.cachedUserId === userId &&
      this.cachedWorkspaceId === workspaceId &&
      this.roleSubject.value
    ) {
      return of(this.roleSubject.value);
    }

    // Return existing loading observable if not forcing refresh
    if (!forceRefresh && this.loading$) {
      return this.loading$;
    }

    // Fetch from API
    const request$ = this.sessionMemberService
      .getUserSessionRole(sessionId, userId, workspaceId)
      .pipe(
        tap((role) => {
          this.roleSubject.next(role);
          this.cachedSessionId = sessionId;
          this.cachedUserId = userId;
          this.cachedWorkspaceId = workspaceId;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.loading$ = request$;
    return request$;
  }

  clear(): void {
    this.roleSubject.next(null);
    this.cachedSessionId = null;
    this.cachedUserId = null;
    this.cachedWorkspaceId = null;
    this.loading$ = null;
  }

  // Call this after role updates to force a refresh
  invalidate(): void {
    this.clear();
  }
}
